/*
 * Operator dashboard (/ops): Kontaktfix staff manage the platform.
 * Separate accounts from customer sign-in: email, password and a mandatory authenticator app (TOTP).
 */
import { z } from "zod";
import type { BillingOverview, SyncRun } from "./api.js";
import {
  type BilledTo,
  type BillingState,
  type ConnectionMethod,
  type ConnectionStatus,
  type DeliveryMode,
  type EmailStatus,
  LANGUAGES,
  type Language,
  type OwnAppCredentialKind,
  type PartnerInvoiceStatus,
  type PartnerLinkStatus,
  type PartnerRole,
  type PlatformAdminStatus,
  type RecipientReason,
  type RecipientStatus,
  type Role,
  type SubscriptionStatus,
} from "./enums.js";
import type { PartnerInvoiceDetails } from "./partner.js";

export const OPS_PASSWORD_MIN = 12;

const email = z.string().trim().toLowerCase().email().max(254);
const password = z.string().min(OPS_PASSWORD_MIN).max(200);
/** Six digits from the authenticator app. Spaces are ignored. */
const totpCode = z
  .string()
  .transform((v) => v.replace(/\s/g, ""))
  .pipe(z.string().regex(/^\d{6}$/));

/* ---------- Sign-in and account ---------- */

export const opsLoginSchema = z.object({ email, password: z.string().min(1).max(200) });

/** Second step: a code from the authenticator app, or one of the recovery codes. */
export const opsCodeSchema = z.object({ code: z.string().trim().min(6).max(32) });

/** Re-confirms identity before sensitive actions (elevated mode). Only authenticator codes. */
export const opsElevateSchema = z.object({ code: totpCode });

export type OpsLoginResult = { next: "code" };

export type OpsMe = {
  id: string;
  email: string;
  name: string;
  language: Language;
  /** Sensitive actions are allowed until this time without asking for a code again. */
  elevatedUntil: string | null;
  recoveryCodesLeft: number;
  /** "zuvo": confirm sensitive actions with a fresh zuvo sign-in; "local" (break-glass): with a code. */
  authMethod: "zuvo" | "local";
};

/** Which sign-ins /ops/login offers. */
export type OpsAuthMethods = { zuvo: boolean; local: boolean };

/** After logout: the zuvo login's end-session URL for zuvo sessions. */
export type OpsLogoutResult = { ok: true; redirectUrl: string | null };

export const opsSetupTokenSchema = z.object({ token: z.string().min(20).max(200) });

export type OpsSetupInfo = { email: string; name: string; kind: "invite" | "reset"; expiresAt: string };

export type OpsTotpEnrollment = {
  /** Base32 secret, for typing into the app by hand. */
  secret: string;
  otpauthUrl: string;
  /** QR code as an SVG data URL. */
  qrCode: string;
};

export const opsSetupCompleteSchema = z.object({
  token: z.string().min(20).max(200),
  password,
  code: totpCode,
});

/** Shown once. Each code signs in once when the authenticator app is not at hand. */
export type OpsRecoveryCodes = { recoveryCodes: string[] };

export const opsPasswordChangeSchema = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: password,
});

/* ---------- Operator accounts ---------- */

export type OpsAdmin = {
  id: string;
  email: string;
  name: string;
  language: Language;
  status: PlatformAdminStatus;
  isSelf: boolean;
  locked: boolean;
  recoveryCodesLeft: number;
  /** Set while a setup link is open (invited or reset). */
  setupExpiresAt: string | null;
  lastLoginAt: string | null;
  createdAt: string;
};

export const opsAdminInviteSchema = z.object({
  email,
  name: z.string().trim().min(1).max(100),
  language: z.enum(LANGUAGES),
});

/** The setup link, shown to the inviting operator in case the email does not arrive. */
export type OpsSetupLink = { setupUrl: string; expiresAt: string };

/* ---------- Overview ---------- */

export type OpsOverview = {
  orgs: {
    total: number;
    connected: number;
    connectionErrors: number;
    suspended: number;
    newLast30Days: number;
  };
  users: { total: number; activeLast30Days: number };
  contacts: number;
  recipients: { total: number; ok: number; problems: number };
  syncRunsLast24h: { total: number; failed: number; withErrors: number };
  emailsLast7Days: { total: number; failed: number };
  /** New organizations per week, oldest first (12 weeks). */
  signupsByWeek: { week: string; count: number }[];
  attention: OpsAttentionItem[];
  /** Subscriptions as mirrored from Stripe. MRR counts active and past-due ones, in CHF. */
  billing: { active: number; trialing: number; pastDue: number; mrr: number; currency: string };
  events: { last30Days: number; undelivered: number };
  leadsLast30Days: number;
  maintenance: boolean;
  /** Where organizations came from (UTM of the sign-up link), last 90 days, most first. */
  acquisition: { source: string; medium: string; campaign: string; orgs: number; subscribed: number }[];
};

export type OpsAttentionItem = {
  kind: "connection_error" | "certificate_expiring" | "sync_failed" | "recipient_problems";
  orgId: string;
  orgName: string;
  detail: string | null;
  at: string | null;
  count?: number;
};

/* ---------- Organizations ---------- */

export type OpsOrg = {
  id: string;
  name: string;
  tenantId: string;
  language: Language;
  connectionStatus: ConnectionStatus;
  connectionMethod: ConnectionMethod;
  suspended: boolean;
  billingState: BillingState;
  contactCount: number;
  recipientCount: number;
  problemCount: number;
  memberCount: number;
  lastSyncAt: string | null;
  createdAt: string;
};

export type OpsOrgMember = {
  id: string;
  name: string;
  email: string;
  role: Role;
  lastLoginAt: string | null;
  createdAt: string;
};

export type OpsOrgDetail = OpsOrg & {
  deliveryMode: DeliveryMode;
  folderName: string;
  suspendedAt: string | null;
  suspendedReason: string | null;
  connectionError: string | null;
  connectionCheckedAt: string | null;
  ownApp: { clientId: string | null; kind: OwnAppCredentialKind | null; certificateExpiresAt: string } | null;
  listCount: number;
  assignmentCount: number;
  recipientTotals: Record<RecipientStatus, number>;
  members: OpsOrgMember[];
  runs: (SyncRun & { error: string | null })[];
  billing: BillingOverview & {
    trialStartedAt: string | null;
    stripeCustomerId: string | null;
    stripeSubscriptionId: string | null;
  };
  /** Contact limits: the operator's override, or null for the default (CONTACT_LIMITS). */
  limits: { maxContactsPerRecipient: number | null; maxContacts: number | null };
};

export const opsOrgUpdateSchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  language: z.enum(LANGUAGES).optional(),
});

export const opsOrgSuspendSchema = z.object({ reason: z.string().trim().min(1).max(500) });

/** Extends (or restarts) the free trial, e.g. for a customer who asked for more time. */
export const opsTrialExtendSchema = z.object({ days: z.number().int().min(1).max(90) });

export const opsOrgDeleteSchema = z.object({ confirmName: z.string() });

/** Raises (or resets with null) the contact limits of one organization (issue #33). */
export const opsOrgLimitsSchema = z.object({
  maxContactsPerRecipient: z.number().int().min(1).max(200_000).nullable(),
  maxContacts: z.number().int().min(1).max(1_000_000).nullable(),
});

export const opsSyncRequestSchema = z.object({ full: z.boolean().default(false) });

/* ---------- People who sign in to the customer app ---------- */

export type OpsUser = {
  id: string;
  name: string;
  email: string;
  tenantId: string;
  createdAt: string;
  lastLoginAt: string | null;
  sessionCount: number;
  orgs: { id: string; name: string; role: Role }[];
};

/* ---------- Delivery across all organizations ---------- */

export type OpsQueue = {
  name: string;
  queued: number;
  active: number;
  deferred: number;
  failed: number;
};

export type OpsSyncRun = SyncRun & { orgId: string; orgName: string; error: string | null };

export type OpsRecipientProblem = {
  orgId: string;
  orgName: string;
  userId: string;
  displayName: string;
  status: RecipientStatus;
  reason: RecipientReason | null;
  reasonDetail: string | null;
  lastAttemptAt: string | null;
};

export type OpsSyncStatus = {
  queues: OpsQueue[];
  runs: OpsSyncRun[];
  problems: OpsRecipientProblem[];
};

/* ---------- Emails ---------- */

export type OpsEmail = {
  id: string;
  orgId: string | null;
  orgName: string | null;
  template: string;
  to: string;
  subject: string;
  status: EmailStatus;
  error: string | null;
  providerId: string | null;
  createdAt: string;
  updatedAt: string;
};

export type OpsEmailTemplate = { name: string; description: string };

export type OpsEmailSettings = {
  /** False when RESEND_API_KEY is missing: emails are only logged. */
  sending: boolean;
  from: string;
  replyTo: string;
  /** Delivery status updates from Resend are verified and recorded. */
  webhook: boolean;
  templates: OpsEmailTemplate[];
};

export const opsTestEmailSchema = z.object({ to: email, language: z.enum(LANGUAGES) });

/* ---------- Operator audit log ---------- */

export type OpsAuditEntry = {
  id: string;
  createdAt: string;
  adminId: string | null;
  adminName: string;
  action: string;
  targetType: string | null;
  targetId: string | null;
  targetLabel: string | null;
  meta: Record<string, unknown> | null;
  ip: string | null;
};

/* ---------- Bug reports ("Report a problem") ---------- */

export const BUG_SEVERITIES = ["minor", "annoying", "blocking"] as const;
export type BugSeverity = (typeof BUG_SEVERITIES)[number];

/** Sent by the app and /ops. Context is collected by the browser, sizes are capped. */
export const bugReportSchema = z.object({
  tried: z.string().trim().min(1).max(2000),
  happened: z.string().trim().min(1).max(4000),
  severity: z.enum(BUG_SEVERITIES),
  context: z
    .object({
      pathname: z.string().max(500).optional(),
      userAgent: z.string().max(500).optional(),
      language: z.string().max(20).optional(),
      timezone: z.string().max(60).optional(),
      viewport: z.string().max(30).optional(),
      theme: z.string().max(10).optional(),
      errors: z
        .array(z.object({ at: z.string().max(40), message: z.string().max(500) }))
        .max(10)
        .optional(),
    })
    .default({}),
});
export type BugReportInput = z.infer<typeof bugReportSchema>;

export type OpsBugReport = {
  id: string;
  createdAt: string;
  source: "app" | "ops";
  orgId: string | null;
  orgName: string | null;
  identityId: string | null;
  severity: BugSeverity;
  tried: string;
  happened: string;
  context: Record<string, unknown>;
  issueUrl: string | null;
  issueError: string | null;
};

/* ---------- Events and leads ---------- */

export type OpsEvent = {
  id: string;
  name: string;
  occurredAt: string;
  orgId: string | null;
  orgName: string | null;
  userId: string | null;
  properties: Record<string, unknown>;
  deliveredAt: string | null;
};

export const LEAD_KINDS = ["contact", "waitlist"] as const;
export type LeadKind = (typeof LEAD_KINDS)[number];

/** The contact and waitlist forms of the landing page. `website` is a honeypot and must stay empty. */
export const leadSchema = z.object({
  kind: z.enum(LEAD_KINDS).default("contact"),
  name: z.string().trim().max(200).default(""),
  email: z.string().trim().toLowerCase().email().max(320),
  company: z.string().trim().max(200).default(""),
  message: z.string().trim().max(5000).default(""),
  locale: z.enum(LANGUAGES).default("de"),
  website: z.string().max(500).default(""),
});

export type OpsLead = {
  id: string;
  createdAt: string;
  kind: LeadKind;
  name: string;
  email: string;
  company: string;
  message: string;
  locale: Language;
};

/* ---------- System and settings ---------- */

/** Maintenance mode: customers see a maintenance page and the API answers 503. Operators pass. */
export type Maintenance = { active: boolean; since: string | null; by: string | null };

export const opsMaintenanceSchema = z.object({ active: z.boolean() });

export type OpsSystem = {
  environment: "production" | "test" | "development";
  /** Commit SHA of the running image, null outside a deployment. */
  version: string | null;
  database: { ok: boolean; latencyMs: number | null };
  integrations: { microsoft: boolean; email: boolean; emailWebhook: boolean; billing: boolean };
  maintenance: Maintenance;
};

/** An IT partner company in the operator dashboard (issue #27). */
export type OpsPartner = {
  id: string;
  name: string;
  code: string;
  suspended: boolean;
  suspendedReason: string | null;
  userCount: number;
  customerCount: number;
  createdAt: string;
  /** Customers billed to the partner. */
  billedCustomerCount: number;
  subscriptionStatus: SubscriptionStatus | null;
  paymentMethodReady: boolean;
  invoice: {
    status: PartnerInvoiceStatus;
    requestedAt: string | null;
    details: PartnerInvoiceDetails | null;
  };
  /** What the partner pays per month for its customers now (excl. VAT). */
  monthly: number;
  /** Customers with a connection problem, a failed last sync or no paid access. */
  attentionCount: number;
  lastLoginAt: string | null;
};

/** One IT partner in detail (/ops/partners/:id). */
export type OpsPartnerDetail = OpsPartner & {
  stripeCustomerId: string | null;
  subscriptionPeriodEnd: string | null;
  users: {
    id: string;
    name: string;
    email: string;
    role: PartnerRole;
    twoFactorEnabled: boolean;
    passkeys: number;
    lastLoginAt: string | null;
    createdAt: string;
    /** Customers a technician can open (partner admins: all). */
    customerCount: number;
  }[];
  invitations: { id: string; email: string; role: PartnerRole; expiresAt: string }[];
  customers: {
    orgId: string;
    name: string;
    /** unlinked: the link ended, the partner still pays until partnerBillingEndsAt. */
    status: PartnerLinkStatus | "unlinked";
    linkedAt: string | null;
    connectionStatus: ConnectionStatus;
    billingState: BillingState;
    billedTo: BilledTo;
    partnerBillingEndsAt: string | null;
    people: number;
    contacts: number;
    lastSync: { status: "running" | "done" | "failed" | "cancelled"; at: string } | null;
  }[];
  reports: { month: string; customers: number; total: number }[];
};

/* ---------- Microsoft Graph requests ---------- */

export const GRAPH_LOG_FILTERS = ["all", "throttled", "errors"] as const;
export type GraphLogFilter = (typeof GRAPH_LOG_FILTERS)[number];

export type OpsGraphRequest = {
  id: number;
  at: string;
  orgId: string;
  orgName: string;
  method: string;
  path: string;
  resource: string;
  mailbox: string | null;
  status: number;
  durationMs: number;
  attempt: number;
  retryAfterS: number | null;
  errorCode: string | null;
  errorMessage: string | null;
  requestId: string | null;
  batchSize: number | null;
  batchThrottled: number | null;
  batchFailed: number | null;
  headers: Record<string, string> | null;
};

/** Counts for one minute (or the whole window). Throttled includes throttled batch sub-requests. */
export type OpsGraphCounts = {
  requests: number;
  throttled: number;
  errors: number;
  avgMs: number;
  maxRetryAfterS: number | null;
};

export type OpsGraphLog = {
  windowMinutes: number;
  totals: OpsGraphCounts;
  minutes: (OpsGraphCounts & { minute: string })[];
  orgs: (OpsGraphCounts & { orgId: string; orgName: string; lastThrottledAt: string | null })[];
  resources: (OpsGraphCounts & { method: string; resource: string })[];
  requests: OpsGraphRequest[];
};
